import { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Copy,
  Check,
  ExternalLink,
  RefreshCw,
  XCircle,
  Send,
  Twitter,
  Linkedin,
  Edit2,
  Save,
  Plus,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Search,
  X as XIcon,
} from 'lucide-react';
import {
  listSocialDrafts,
  markPosted,
  skipSocialDraft,
  regenerateSocialDraft,
  editSocialDraft,
  createAdHocSocialDraft,
  pushToBuffer,
  bulkPushToBuffer,
  getPushSettings,
  updatePushSettings,
  type SocialDraft,
  type SocialPlatform,
  type SocialStatus,
} from '../lib/api/social.js';
import { getArticle } from '../lib/api/articles.js';
import { getLinkedInStatus, postLinkedInNative } from '../lib/api/linkedin.js';
import { getArticlePublishTargets } from '../lib/api/egress.js';
import { handleMutationConflict } from '../lib/mutationConflict.js';
import { useWebSocket } from '../hooks/useWebSocket.js';
import { usePresence } from '../hooks/usePresence.js';
import { PresencePill } from '../components/PresencePill.js';
import { DateBar } from '../components/DateBar.js';
import { EntityGroupFilter, useEntityGroupPill } from '../components/EntityGroupFilter.js';
import { PublicationFilter, usePublicationPill } from '../components/PublicationFilter.js';
import { CoverageBadges } from '../components/CoverageBadges.js';

// 'shortlisted' is a pseudo-tab: it fetches status='draft' and filters to the
// rows the shortlist worker stamped (shortlisted_at != null), ordered by rank.
type TabValue = SocialStatus | 'shortlisted';

const STATUS_OPTIONS: { value: TabValue; label: string }[] = [
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'draft', label: 'Drafts' },
  // 'approved' == pushed to Buffer, awaiting the reviewer there.
  { value: 'approved', label: 'In Buffer' },
  { value: 'posted', label: 'Posted' },
  { value: 'failed', label: 'Failed' },
  // Operator-facing label: 'Hidden' for status='skipped'. DB / API value
  // stays 'skipped' so existing rows and the skip endpoint don't churn.
  { value: 'skipped', label: 'Hidden' },
];

/** Operator-facing display labels keyed by SocialStatus. Used in per-row
 *  badges + drawer status lines. DB value stays in the type for filter
 *  routing and audit. */
const STATUS_DISPLAY_LABEL: Record<SocialStatus, string> = {
  draft: 'draft',
  posted: 'posted',
  skipped: 'hidden',
  approved: 'approved',
  failed: 'failed',
};

const PLATFORM_LABELS: Record<string, { label: string; icon: typeof Twitter; openUrl: string }> = {
  twitter: { label: 'X', icon: Twitter, openUrl: 'https://twitter.com/compose/tweet' },
  linkedin: { label: 'LinkedIn', icon: Linkedin, openUrl: 'https://www.linkedin.com/feed/' },
};

const ALL_PLATFORMS: SocialPlatform[] = ['twitter', 'linkedin'];

const STATUS_BADGE: Record<SocialStatus, string> = {
  draft: 'bg-primary-100 text-primary-700',
  posted: 'bg-success-100 text-success-700',
  skipped: 'bg-grey-100 text-grey-600',
  approved: 'bg-primary-100 text-primary-700',
  failed: 'bg-danger-100 text-danger-700',
};

/**
 * Daily Buffer push cap control (migration 236). Enable/disable the per-site
 * daily push limit and set its value. Read/written via the /social admin API;
 * the publishing worker enforces it per masthead per Sydney day.
 */
function PushCapControl() {
  const queryClient = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ['social', 'push-settings'],
    queryFn: getPushSettings,
    staleTime: 60_000,
  });

  const [enabled, setEnabled] = useState<boolean>(true);
  const [limit, setLimit] = useState<string>('50');

  // Sync local editor state from the server whenever it (re)loads.
  useEffect(() => {
    if (settings) {
      setEnabled(settings.daily_limit_enabled);
      setLimit(String(settings.daily_limit_per_site));
    }
  }, [settings]);

  const mutation = useMutation({
    mutationFn: updatePushSettings,
    onSuccess: (s) => {
      queryClient.setQueryData(['social', 'push-settings'], s);
      queryClient.invalidateQueries({ queryKey: ['social', 'push-settings'] });
    },
  });

  const parsedLimit = Math.max(0, Math.min(1000, Math.trunc(Number(limit) || 0)));
  const dirty =
    !!settings &&
    (enabled !== settings.daily_limit_enabled || parsedLimit !== settings.daily_limit_per_site);

  return (
    <div className="flex items-center gap-2 rounded-md border border-grey-200 bg-grey-50 px-3 py-1.5 text-sm">
      <label className="flex items-center gap-1.5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-4 w-4 rounded border-grey-300 text-primary-600 focus:ring-primary-400"
        />
        <span className="text-grey-700">Daily Buffer push cap</span>
      </label>
      <input
        type="number"
        min={0}
        max={1000}
        value={limit}
        disabled={!enabled}
        onChange={(e) => setLimit(e.target.value)}
        className="w-16 rounded border border-grey-300 px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:bg-grey-100 disabled:text-grey-400"
        aria-label="Daily push limit per site"
      />
      <span className="text-xs text-grey-500">per site / day</span>
      <button
        type="button"
        disabled={!dirty || mutation.isPending}
        onClick={() => mutation.mutate({ daily_limit_enabled: enabled, daily_limit_per_site: parsedLimit })}
        className="rounded-md bg-primary-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-grey-300"
      >
        {mutation.isPending ? 'Saving…' : 'Save'}
      </button>
      {mutation.isError && <span className="text-xs text-danger-600">Save failed</span>}
    </div>
  );
}

export function SocialDraftsPage() {
  const [searchParams] = useSearchParams();
  const articleId = searchParams.get('articleId');

  if (articleId) {
    return <ArticleFocusedView articleId={articleId} />;
  }

  return <AllDraftsView />;
}

// =============================================================================
// Date helpers
// =============================================================================

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayYMD(): string {
  return ymd(new Date());
}

function shiftDays(date: string, deltaDays: number): string {
  const d = new Date(date + 'T00:00:00');
  d.setDate(d.getDate() + deltaDays);
  return ymd(d);
}

// =============================================================================
// Default view — date bar + entity search + status tabs
// =============================================================================

function AllDraftsView() {
  const [statusFilter, setStatusFilter] = useState<TabValue>('draft');
  const [anchorDate, setAnchorDate] = useState<string>(todayYMD()); // right-most day in the strip
  const [selectedDate, setSelectedDate] = useState<string>(todayYMD());
  const [searchInput, setSearchInput] = useState('');
  const queryClient = useQueryClient();

  // Debounce the search input → effective query
  const debouncedSearch = useDebounced(searchInput.trim(), 300);
  const searchActive = debouncedSearch.length > 0;

  // Entity-group pill (sticky across all four list pages via localStorage).
  const [entityGroup] = useEntityGroupPill();
  const entityGroupParam = entityGroup === 'all' ? undefined : entityGroup;

  // Platform filter (page-local) — stacks with the entity-group / Campaigns pill.
  const [platform, setPlatform] = useState<'all' | 'twitter' | 'linkedin'>('all');
  const platformParam = platform === 'all' ? undefined : platform;

  // Publication filter (sticky across Notifications + Social via localStorage).
  const { targetId: publicationTargetId } = usePublicationPill();

  const days = useMemo(() => {
    // 7-day strip ending on anchorDate
    const out: string[] = [];
    for (let i = 6; i >= 0; i--) out.push(shiftDays(anchorDate, -i));
    return out;
  }, [anchorDate]);

  const queryKey = searchActive
    ? ['social-drafts', 'search', debouncedSearch, statusFilter, entityGroup, platform, publicationTargetId]
    : ['social-drafts', 'date', selectedDate, statusFilter, entityGroup, platform, publicationTargetId];

  // Realtime: backend emits on PATCH / mark-posted / skip / regenerate.
  // WebSocketContext auto-invalidates `['social-drafts']` (matches the
  // queryKey prefix above). The 5-min poll is a safety net for WS drops.
  useWebSocket({ channels: ['assayerai:social-drafts'] });

  // The Shortlisted pseudo-tab fetches the day's drafts and filters client-side
  // to the worker-stamped rows; the API only knows real statuses.
  const shortlistedView = statusFilter === 'shortlisted';
  const apiStatus: SocialStatus | 'all' = shortlistedView ? 'draft' : statusFilter;

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () =>
      listSocialDrafts(
        searchActive
          ? { q: debouncedSearch, status: 'all', entity_group: entityGroupParam, platform: platformParam, targetId: publicationTargetId ?? undefined, limit: 200 }
          : { date: selectedDate, status: apiStatus, entity_group: entityGroupParam, platform: platformParam, targetId: publicationTargetId ?? undefined, limit: 200 },
      ),
    refetchInterval: 5 * 60_000,
  });

  const rawDrafts = data?.drafts ?? [];
  const drafts = shortlistedView
    ? rawDrafts
        .filter(d => d.shortlisted_at != null && d.platform === 'linkedin')
        .sort(
          (a, b) =>
            (a.target_name ?? '').localeCompare(b.target_name ?? '') ||
            (a.shortlist_rank ?? 0) - (b.shortlist_rank ?? 0),
        )
    : rawDrafts;

  // Bulk push the confirmed shortlist. Only LinkedIn drafts still in 'draft'
  // are eligible (approved/posted are already in Buffer).
  const shortlistIds = drafts
    .filter(d => d.status === 'draft' && d.platform === 'linkedin')
    .map(d => d.id);
  const bulkPushMutation = useMutation({
    mutationFn: () => bulkPushToBuffer(shortlistIds),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['social-drafts'] }),
  });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-grey-900">Social Drafts</h1>
            <p className="text-sm text-grey-600 mt-1">
              Auto-composed Twitter/X and LinkedIn posts. Copy the text, paste it into the platform's
              compose box, then mark the draft as posted.
            </p>
          </div>
          <PushCapControl />
        </div>
      </header>

      {/* Publication filter — sticky across Notifications + Social. */}
      <div className="mb-3">
        <PublicationFilter />
      </div>

      {/* Entity-group pills (incl. Campaigns) + platform filter. Both stack. */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <EntityGroupFilter />
        <div className="flex items-center gap-1">
          {([['all', 'All'], ['linkedin', 'LinkedIn'], ['twitter', 'X']] as const).map(
            ([val, label]) => (
              <button
                key={val}
                onClick={() => setPlatform(val)}
                className={`text-sm px-3 py-1.5 rounded-lg border ${
                  platform === val
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-surface text-grey-700 border-grey-300'
                }`}
              >
                {label}
              </button>
            ),
          )}
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
          <input
            type="search"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search by company code or article title…"
            className="w-full pl-9 pr-9 py-2 text-sm border border-grey-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-grey-400 hover:text-grey-700"
              aria-label="Clear search"
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {searchActive && (
          <span className="text-xs text-grey-500">Searching across all dates & statuses</span>
        )}
      </div>

      {/* Date bar — hidden when search is active */}
      {!searchActive && (
        <DateBar
          days={days}
          selectedDate={selectedDate}
          onSelect={setSelectedDate}
          onShiftWeek={delta => setAnchorDate(shiftDays(anchorDate, delta * 7))}
          onToday={() => {
            setAnchorDate(todayYMD());
            setSelectedDate(todayYMD());
          }}
        />
      )}

      {/* Status tabs — hidden when search is active (we show per-row badges instead) */}
      {!searchActive && (
        <div className="flex gap-2 mt-4 mb-4 border-b border-grey-200 overflow-x-auto overscroll-x-contain">
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap flex-shrink-0 ${
                statusFilter === opt.value
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-grey-600 hover:text-grey-900'
              }`}
            >
              {opt.label}
              {statusFilter === opt.value && data?.count != null && (
                <span className="ml-2 text-xs text-grey-500">({data.count})</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Shortlist action bar — confirm the auto-selection into Buffer in bulk. */}
      {shortlistedView && !searchActive && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 p-3">
          <div className="text-sm text-grey-700">
            {shortlistIds.length > 0 ? (
              <>
                <strong>{shortlistIds.length}</strong> shortlisted LinkedIn draft
                {shortlistIds.length === 1 ? '' : 's'} ready to send to Buffer.
              </>
            ) : (
              <>No unsent shortlisted drafts for this day.</>
            )}
          </div>
          <button
            onClick={() => {
              if (confirm(`Send ${shortlistIds.length} shortlisted draft(s) to Buffer?`)) {
                bulkPushMutation.mutate();
              }
            }}
            disabled={shortlistIds.length === 0 || bulkPushMutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
          >
            <Send className="h-4 w-4" />
            {bulkPushMutation.isPending ? 'Sending…' : 'Send shortlist to Buffer'}
          </button>
          {bulkPushMutation.data && (
            <span className="text-xs text-grey-600">
              {Object.entries(bulkPushMutation.data.summary)
                .map(([k, v]) => `${v} ${k}`)
                .join(', ')}
            </span>
          )}
        </div>
      )}

      {isLoading && <div className="text-sm text-grey-500">Loading drafts…</div>}
      {error && (
        <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700">
          Failed to load drafts: {(error as Error).message}
        </div>
      )}

      {!isLoading && drafts.length === 0 && (
        <div className="rounded-lg border border-grey-200 bg-grey-50 p-8 text-center text-sm text-grey-600">
          {searchActive
            ? <>No drafts match <strong>"{debouncedSearch}"</strong>.</>
            : <>No drafts on this date.</>}
          {!searchActive && statusFilter === 'draft' && (
            <div className="mt-2 text-grey-500">
              New drafts appear here automatically when an article is promoted to a pub's main branch.
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        {drafts.map(d => (
          <DraftCard
            key={d.id}
            draft={d}
            showStatusBadge={searchActive}
            queryKeyToInvalidate={queryKey}
            queryClient={queryClient}
          />
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// useDebounced
// =============================================================================

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

// =============================================================================
// Article-focused view — drafts + create-buttons per (target × platform)
// =============================================================================

function ArticleFocusedView({ articleId }: { articleId: string }) {
  const queryClient = useQueryClient();

  // Same realtime subscription as the parent list — WS push covers the
  // by-article view too (auto-invalidate matches the `['social-drafts',
  // 'by-article', articleId]` key by prefix).
  useWebSocket({ channels: ['assayerai:social-drafts'] });

  const { data: article } = useQuery({
    queryKey: ['article', articleId],
    queryFn: () => getArticle(articleId),
  });

  const { data: publishTargets } = useQuery({
    queryKey: ['article-publish-targets', articleId],
    queryFn: () => getArticlePublishTargets(articleId),
  });

  const { data: draftsData, isLoading } = useQuery({
    queryKey: ['social-drafts', 'by-article', articleId],
    queryFn: () => listSocialDrafts({ articleId, status: 'all', limit: 50 }),
    refetchInterval: 5 * 60_000,
  });

  const liveTargets = (publishTargets || []).filter(
    (pt: any) => pt.destination_type === 'main_branch',
  );

  const drafts = draftsData?.drafts ?? [];
  const draftKey = (targetId: string, platform: SocialPlatform) =>
    `${targetId}::${platform}`;
  const draftIndex = new Map(
    drafts.map(d => [draftKey(d.target_id, d.platform), d]),
  );

  const createMutation = useMutation({
    mutationFn: ({ targetName, platform }: { targetName: string; platform: SocialPlatform }) =>
      createAdHocSocialDraft(articleId, targetName, platform),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-drafts', 'by-article', articleId] });
    },
  });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-6">
        <Link
          to="/social"
          className="inline-flex items-center gap-1 text-sm text-grey-600 hover:text-grey-900 mb-3"
        >
          <ArrowLeft className="h-4 w-4" /> All drafts
        </Link>
        <h1 className="text-2xl font-semibold text-grey-900">Social posts for this article</h1>
        {article && (
          <p className="text-sm text-grey-700 mt-1">
            <Link
              to={`/articles/${articleId}`}
              className="hover:text-primary-700 hover:underline"
            >
              {article.article_title}
            </Link>
          </p>
        )}
        <p className="text-xs text-grey-500 mt-1">
          One draft per (live target × platform). Edit the text in place, regenerate against the
          current article + handles, or hide.
        </p>
      </header>

      {isLoading && <div className="text-sm text-grey-500">Loading…</div>}

      {!isLoading && liveTargets.length === 0 && (
        <div className="rounded-lg border border-grey-200 bg-grey-50 p-8 text-center text-sm text-grey-600">
          This article isn't live on any publication yet. Promote it first, then come back to
          generate social posts.
        </div>
      )}

      <div className="space-y-6">
        {liveTargets.map((target: any) => (
          <section key={target.target_id}>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-grey-500 mb-3">
              {target.target_name}
              {target.external_url && (
                <a
                  href={target.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 text-grey-400 hover:text-grey-700"
                >
                  <ExternalLink className="h-3.5 w-3.5 inline" />
                </a>
              )}
            </h2>
            <div className="space-y-3">
              {ALL_PLATFORMS.map(platform => {
                const existing = draftIndex.get(draftKey(target.target_id, platform));
                if (existing) {
                  return (
                    <DraftCard
                      key={`${target.target_id}-${platform}`}
                      draft={existing}
                      defaultExpanded
                      queryKeyToInvalidate={['social-drafts', 'by-article', articleId]}
                      queryClient={queryClient}
                    />
                  );
                }
                const platformMeta = PLATFORM_LABELS[platform];
                const PlatformIcon = platformMeta.icon;
                const pending = createMutation.isPending;
                return (
                  <div
                    key={`${target.target_id}-${platform}`}
                    className="rounded-lg border border-dashed border-grey-300 bg-surface p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3 text-sm text-grey-600">
                      <PlatformIcon className="h-4 w-4" />
                      <span>No {platformMeta.label} draft yet for this article on {target.target_name}.</span>
                    </div>
                    <button
                      disabled={pending}
                      onClick={() =>
                        createMutation.mutate({ targetName: target.target_name, platform })
                      }
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
                    >
                      <Plus className="h-4 w-4" />
                      Create {platformMeta.label} draft
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// DraftCard — collapsible by default, full surface when expanded
// =============================================================================

interface DraftCardProps {
  draft: SocialDraft;
  /** When true, ignore default collapsed state and render expanded. */
  defaultExpanded?: boolean;
  /** When true, show a small badge for the draft's status (used in search results). */
  showStatusBadge?: boolean;
  queryKeyToInvalidate: any[];
  queryClient: ReturnType<typeof useQueryClient>;
}

function DraftCard({
  draft,
  defaultExpanded = false,
  showStatusBadge = false,
  queryKeyToInvalidate,
  queryClient,
}: DraftCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);
  const [copiedImg, setCopiedImg] = useState(false);
  const [showMarkPosted, setShowMarkPosted] = useState(false);
  const [platformUrl, setPlatformUrl] = useState('');
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState(draft.composed_text);

  // Soft-presence beacon: only beacon while THIS card is expanded (the
  // social UX has no separate drawer — the expanded card is the
  // equivalent surface). Passing null when collapsed makes usePresence a
  // no-op, so collapsed cards don't churn presence rows.
  usePresence({
    resourceType: 'social_post',
    resourceId: expanded ? draft.id : null,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeyToInvalidate });

  const onConflict = (e: any) => handleMutationConflict(e, queryClient, [queryKeyToInvalidate]);

  const markPostedMutation = useMutation({
    mutationFn: (platformPostUrl?: string) => markPosted(draft.id, platformPostUrl),
    onSuccess: invalidate,
    onError: onConflict,
  });
  const skipMutation = useMutation({
    mutationFn: () => skipSocialDraft(draft.id),
    onSuccess: invalidate,
    onError: onConflict,
  });
  const regenMutation = useMutation({
    mutationFn: () => regenerateSocialDraft(draft.id),
    onSuccess: invalidate,
    onError: onConflict,
  });
  const pushMutation = useMutation({
    mutationFn: () => pushToBuffer(draft.id),
    onSuccess: invalidate,
    onError: onConflict,
  });
  // P5 (CS538): native posting to the LinkedIn TEST Page (DevTestCo) for the
  // Standard-Tier screencast. Shown only when the backend flag is on; the
  // status call is super_admin only, so other roles never see the button.
  const nativeStatus = useQuery({
    queryKey: ['linkedin-native-posting'],
    queryFn: getLinkedInStatus,
    enabled: draft.platform === 'linkedin',
    retry: false,
    staleTime: 5 * 60_000,
  });
  const nativeMutation = useMutation({
    mutationFn: () => postLinkedInNative(draft.id),
  });
  const editMutation = useMutation({
    mutationFn: (text: string) => editSocialDraft(draft.id, text),
    onSuccess: (resp) => {
      setEditing(false);
      setDraftText(resp.draft.composed_text);
      invalidate();
    },
    onError: onConflict,
  });

  const platform = PLATFORM_LABELS[draft.platform] ?? {
    label: draft.platform,
    icon: Send,
    openUrl: '#',
  };
  const PlatformIcon = platform.icon;

  const copyText = async () => {
    await navigator.clipboard?.writeText(draft.composed_text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const copyImage = async () => {
    if (!draft.image_url) return;
    await navigator.clipboard?.writeText(draft.image_url);
    setCopiedImg(true);
    setTimeout(() => setCopiedImg(false), 1500);
  };

  // First two lines of composed_text used as the collapsed preview. Strips
  // blank lines so we don't waste a line on the gap between headline + body.
  const previewLines = useMemo(() => {
    const lines = draft.composed_text.split('\n').filter(l => l.trim().length > 0);
    return lines.slice(0, 2);
  }, [draft.composed_text]);

  return (
    <div className="rounded-lg border border-grey-200 bg-surface shadow-sm">
      {/* Header — always visible, click to toggle */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full text-left p-4 flex items-start justify-between gap-3 hover:bg-grey-50 transition-colors rounded-t-lg"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-grey-500 mb-1 flex-wrap">
            <PlatformIcon className="h-3.5 w-3.5" />
            <span className="font-semibold uppercase tracking-wide">{platform.label}</span>
            <span>·</span>
            <span className="text-grey-700 font-medium">{draft.target_name ?? draft.target_id}</span>
            {draft.company_code && (
              <>
                <span>·</span>
                <span className="font-mono text-grey-700">{draft.company_code}</span>
              </>
            )}
            <CoverageBadges tiers={draft.coverage_tiers} />
            {showStatusBadge && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide font-semibold ${STATUS_BADGE[draft.status]}`}>
                {STATUS_DISPLAY_LABEL[draft.status] ?? draft.status}
              </span>
            )}
            <PresencePill resourceType="social_post" resourceId={draft.id} compact />
          </div>
          <div className="text-base font-semibold text-grey-900 line-clamp-2">
            {draft.article_title ?? draft.article_id}
          </div>
          {!expanded && previewLines.length > 1 && (
            <div className="mt-1 text-xs text-grey-500 font-mono line-clamp-1">
              {previewLines[1]}
            </div>
          )}
        </div>
        <div className="flex items-start gap-2 shrink-0">
          <div className="text-xs text-grey-400 whitespace-nowrap mt-0.5">
            {new Date(draft.created_at).toLocaleString()}
          </div>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-grey-400 mt-0.5" />
          ) : (
            <ChevronDown className="h-4 w-4 text-grey-400 mt-0.5" />
          )}
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="px-4 pb-4">
          <textarea
            readOnly={!editing}
            value={editing ? draftText : draft.composed_text}
            onChange={editing ? e => setDraftText(e.target.value) : undefined}
            className={`w-full font-mono text-sm border rounded-md p-3 mb-3 resize-y min-h-[110px] ${
              editing
                ? 'bg-surface border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200'
                : 'bg-grey-50 border-grey-200'
            }`}
          />

          {draft.image_url && !editing && (
            <div className="flex items-center gap-3 mb-3 text-xs text-grey-600">
              <span className="text-grey-500">Hero:</span>
              <img
                src={draft.image_url}
                alt=""
                className="h-12 w-20 object-cover rounded border border-grey-200"
              />
              <button
                onClick={copyImage}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-grey-700 hover:bg-grey-100"
              >
                {copiedImg ? <Check className="h-3.5 w-3.5 text-success-600" /> : <Copy className="h-3.5 w-3.5" />}
                {copiedImg ? 'Copied' : 'Copy image URL'}
              </button>
            </div>
          )}

          {editing ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => editMutation.mutate(draftText)}
                disabled={editMutation.isPending || draftText.trim().length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
              >
                <Save className="h-4 w-4" />
                Save changes
              </button>
              <button
                onClick={() => { setEditing(false); setDraftText(draft.composed_text); }}
                className="px-3 py-2 text-sm text-grey-600 hover:text-grey-900"
              >
                Cancel
              </button>
            </div>
          ) : draft.status === 'draft' ? (
            <div className="flex flex-wrap items-center gap-2">
              {draft.platform === 'linkedin' && (
                <button
                  onClick={() => pushMutation.mutate()}
                  disabled={pushMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
                  title="Send this draft to Buffer as a draft for review"
                >
                  <Send className="h-4 w-4" />
                  {pushMutation.isPending ? 'Sending…' : 'Send to Buffer'}
                </button>
              )}
              {draft.platform === 'linkedin' && nativeStatus.data?.native_posting_enabled && (
                <button
                  onClick={() => { if (confirm('Post this draft natively to the LinkedIn test Page?')) nativeMutation.mutate(); }}
                  disabled={nativeMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-60"
                  title="Posts through the LinkedIn API to the test Page only; this draft and Buffer are not changed"
                >
                  <Linkedin className="h-4 w-4" />
                  {nativeMutation.isPending ? 'Posting…' : 'Post natively (test Page)'}
                </button>
              )}
              <button
                onClick={copyText}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md ${
                  draft.platform === 'linkedin'
                    ? 'border border-grey-300 text-grey-700 hover:bg-grey-50'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy text'}
              </button>
              <a
                href={platform.openUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border border-grey-300 text-grey-700 hover:bg-grey-50"
              >
                <ExternalLink className="h-4 w-4" />
                Open {platform.label}
              </a>
              <a
                href={draft.live_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border border-grey-300 text-grey-700 hover:bg-grey-50"
              >
                <ExternalLink className="h-4 w-4" />
                Article
              </a>
              <button
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border border-grey-300 text-grey-700 hover:bg-grey-50"
              >
                <Edit2 className="h-4 w-4" />
                Edit
              </button>
              <button
                onClick={() => regenMutation.mutate()}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border border-grey-300 text-grey-700 hover:bg-grey-50"
              >
                <RefreshCw className="h-4 w-4" />
                Regenerate
              </button>
              <button
                onClick={() => setShowMarkPosted(v => !v)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border border-success-300 text-success-700 hover:bg-success-50"
              >
                <Check className="h-4 w-4" />
                Mark as posted
              </button>
              <button
                onClick={() => { if (confirm('Hide this draft?')) skipMutation.mutate(); }}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md text-danger-600 hover:bg-danger-50"
                title="Hide this draft from the queue (preserved server-side as 'skipped' for audit)"
              >
                <XCircle className="h-4 w-4" />
                Hide
              </button>
            </div>
          ) : null}

          {nativeMutation.data && (
            <div className="mt-3 rounded-md border border-success-300 bg-success-50 p-3 text-sm text-success-800">
              Posted to the test Page: <code>{nativeMutation.data.post_urn}</code>{' '}
              <a href={nativeMutation.data.url} target="_blank" rel="noopener noreferrer" className="underline">
                Open on LinkedIn
              </a>
              {nativeMutation.data.image_note ? ` · ${nativeMutation.data.image_note}` : ''}
              {` · ${nativeMutation.data.mentions} mention${nativeMutation.data.mentions === 1 ? '' : 's'}`}
            </div>
          )}
          {nativeMutation.isError && (
            <div className="mt-3 rounded-md border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
              Native post failed: {(nativeMutation.error as any)?.response?.data?.detail ?? String(nativeMutation.error)}
            </div>
          )}

          {showMarkPosted && draft.status === 'draft' && !editing && (
            <div className="mt-3 rounded-md border border-grey-200 bg-grey-50 p-3 flex flex-wrap items-center gap-2">
              <input
                type="url"
                placeholder="Optional: paste the platform post URL"
                value={platformUrl}
                onChange={e => setPlatformUrl(e.target.value)}
                className="flex-1 min-w-0 w-full sm:w-auto sm:min-w-[300px] px-3 py-2 text-sm border border-grey-300 rounded-md"
              />
              <button
                onClick={() => {
                  markPostedMutation.mutate(platformUrl.trim() || undefined);
                  setShowMarkPosted(false);
                  setPlatformUrl('');
                }}
                className="px-3 py-2 text-sm font-medium rounded-md bg-success-600 text-white hover:bg-success-700"
              >
                Confirm posted
              </button>
              <button
                onClick={() => setShowMarkPosted(false)}
                className="px-3 py-2 text-sm text-grey-600 hover:text-grey-900"
              >
                Cancel
              </button>
            </div>
          )}

          {draft.status === 'posted' && draft.platform_post_url && (
            <div className="text-xs text-grey-600">
              Posted to:{' '}
              <a href={draft.platform_post_url} target="_blank" rel="noopener noreferrer" className="text-primary-700 hover:underline">
                {draft.platform_post_url}
              </a>
              {draft.posted_at && <span className="ml-2 text-grey-500">at {new Date(draft.posted_at).toLocaleString()}</span>}
            </div>
          )}
          {draft.status === 'posted' && !draft.platform_post_url && (
            <div className="text-xs text-grey-500">
              Marked posted {draft.posted_at ? `at ${new Date(draft.posted_at).toLocaleString()}` : ''} (no platform URL captured)
            </div>
          )}
          {draft.status === 'skipped' && (
            <div className="text-xs text-grey-500">
              Hidden {draft.skipped_at ? `at ${new Date(draft.skipped_at).toLocaleString()}` : ''}
            </div>
          )}

          {/* A draft that is mid-retry after a transient Buffer failure. */}
          {draft.status === 'draft' && (draft.push_attempts ?? 0) > 0 && (
            <div className="mt-1 text-xs text-warning-700">
              Retrying Buffer push (attempt {draft.push_attempts})
              {draft.last_error ? ` — ${draft.last_error}` : ''}
            </div>
          )}

          {/* Pushed to Buffer, awaiting the reviewer's approve/schedule there. */}
          {draft.status === 'approved' && (
            <div className="text-xs text-grey-600">
              In Buffer — awaiting review
              {draft.pushed_to_buffer_at && (
                <span className="ml-2 text-grey-500">
                  pushed {new Date(draft.pushed_to_buffer_at).toLocaleString()}
                </span>
              )}
            </div>
          )}

          {/* Terminal push failure — fall back to manual copy-paste. */}
          {draft.status === 'failed' && (
            <div className="space-y-2">
              <div className="rounded-md border border-danger-200 bg-danger-50 p-2 text-xs text-danger-700">
                Buffer push failed{draft.last_error ? `: ${draft.last_error}` : ''}. Copy the text
                and post manually.
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={copyText}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy text'}
                </button>
                <a
                  href={platform.openUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border border-grey-300 text-grey-700 hover:bg-grey-50"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open {platform.label}
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
